import { useState } from "react";
import { CrossPromo } from "../components/CrossPromo.jsx";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { HomeButton } from "../components/HomeButton.jsx";
import { formatNum } from "../lib/formatNum.js";

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

const SKY = "#0EA5E9";
const BLUE = "#3B82F6";
const SKY_LIGHT = "#7DD3FC";

const MODES = [
  { id: "of", label: "% of Number", icon: "🔢", desc: "What is X% of Y?", question: "What is", between: "% of", suffix: "?" },
  { id: "is", label: "X is what %", icon: "📊", desc: "X is what % of Y?", question: "", between: "is what % of", suffix: "?" },
  { id: "change", label: "% Change", icon: "📈", desc: "From X to Y", question: "From", between: "to", suffix: "" },
  { id: "tip", label: "Tip Calculator", icon: "💰", desc: "Bill + tip split", question: "Bill:", between: "Tip %:", suffix: "" },
];

function NumInput({ value, onChange, placeholder, label, prefix, suffix, style: s = {} }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, ...s }}>
      {label && <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</label>}
      <div style={{ position: "relative" }}>
        {prefix && <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#4B5563" }}>{prefix}</span>}
        <input type="number" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{
            width: "100%", padding: "12px 14px",
            paddingLeft: prefix ? 28 : 14, paddingRight: suffix ? 36 : 14,
            borderRadius: 10, border: `1px solid ${rgba(SKY, 0.12)}`,
            background: "rgba(0,0,0,0.3)", color: "#E2E8F0", fontSize: 16,
            fontFamily: "'DM Sans', system-ui, sans-serif", outline: "none", boxSizing: "border-box",
          }} />
        {suffix && <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#4B5563" }}>{suffix}</span>}
      </div>
    </div>
  );
}

function Result({ label, value, sub, color = SKY_LIGHT }) {
  return (
    <div style={{
      padding: "20px 16px", borderRadius: 14, textAlign: "center", flex: 1,
      background: `linear-gradient(135deg, ${rgba(color, 0.08)}, ${rgba(color, 0.03)})`,
      border: `1px solid ${rgba(color, 0.15)}`,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color, letterSpacing: "-0.5px" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#4B5563", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ─── Percentage Of ───
function PercentOf() {
  const [pct, setPct] = useState("");
  const [num, setNum] = useState("");
  const p = parseFloat(pct), n = parseFloat(num);
  const result = (!isNaN(p) && !isNaN(n)) ? (p / 100 * n) : null;

  return (
    <div>
      <p style={{ fontSize: 15, color: "#6B7280", marginTop: 0, marginBottom: 20, lineHeight: 1.6 }}>
        Calculate what a percentage of a number is.
      </p>
      <div style={{ display: "flex", alignItems: "end", gap: 10, marginBottom: 20 }}>
        <div style={{ fontSize: 14, color: "#6B7280", paddingBottom: 14 }}>What is</div>
        <NumInput value={pct} onChange={setPct} placeholder="15" suffix="%" style={{ flex: 1 }} />
        <div style={{ fontSize: 14, color: "#6B7280", paddingBottom: 14 }}>of</div>
        <NumInput value={num} onChange={setNum} placeholder="200" style={{ flex: 1 }} />
        <div style={{ fontSize: 14, color: "#6B7280", paddingBottom: 14 }}>?</div>
      </div>
      {result !== null && (
        <Result label="Result" value={formatNum(result, 2)}
          sub={`${pct}% of ${num} = ${formatNum(result, 2)}`} />
      )}
    </div>
  );
}

// ─── X is what % of Y ───
function PercentIs() {
  const [x, setX] = useState("");
  const [y, setY] = useState("");
  const a = parseFloat(x), b = parseFloat(y);
  const result = (!isNaN(a) && !isNaN(b) && b !== 0) ? (a / b * 100) : null;

  return (
    <div>
      <p style={{ fontSize: 15, color: "#6B7280", marginTop: 0, marginBottom: 20, lineHeight: 1.6 }}>
        Find what percentage one number is of another.
      </p>
      <div style={{ display: "flex", alignItems: "end", gap: 10, marginBottom: 20 }}>
        <NumInput value={x} onChange={setX} placeholder="30" style={{ flex: 1 }} />
        <div style={{ fontSize: 14, color: "#6B7280", paddingBottom: 14 }}>is what % of</div>
        <NumInput value={y} onChange={setY} placeholder="200" style={{ flex: 1 }} />
        <div style={{ fontSize: 14, color: "#6B7280", paddingBottom: 14 }}>?</div>
      </div>
      {result !== null && (
        <Result label="Result" value={`${formatNum(result, 2)}%`}
          sub={`${x} is ${formatNum(result, 2)}% of ${y}`} />
      )}
    </div>
  );
}

// ─── Percent Change ───
function PercentChange() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const a = parseFloat(from), b = parseFloat(to);
  const result = (!isNaN(a) && !isNaN(b) && a !== 0) ? ((b - a) / Math.abs(a) * 100) : null;
  const isIncrease = result !== null && result >= 0;

  return (
    <div>
      <p style={{ fontSize: 15, color: "#6B7280", marginTop: 0, marginBottom: 20, lineHeight: 1.6 }}>
        Calculate the percentage increase or decrease between two numbers.
      </p>
      <div style={{ display: "flex", alignItems: "end", gap: 10, marginBottom: 20 }}>
        <div style={{ fontSize: 14, color: "#6B7280", paddingBottom: 14 }}>From</div>
        <NumInput value={from} onChange={setFrom} placeholder="80" style={{ flex: 1 }} />
        <div style={{ fontSize: 14, color: "#6B7280", paddingBottom: 14 }}>to</div>
        <NumInput value={to} onChange={setTo} placeholder="120" style={{ flex: 1 }} />
      </div>
      {result !== null && (
        <div style={{ display: "flex", gap: 12 }}>
          <Result label="% Change"
            value={`${isIncrease ? "+" : ""}${formatNum(result, 2)}%`}
            color={isIncrease ? "#10B981" : "#F43F5E"}
            sub={isIncrease ? "Increase" : "Decrease"} />
          <Result label="Difference"
            value={formatNum(b - a, 2)}
            sub={`${to} - ${from}`} />
        </div>
      )}
    </div>
  );
}

// ─── Tip Calculator ───
function TipCalc() {
  const [bill, setBill] = useState("");
  const [tip, setTip] = useState("18");
  const [split, setSplit] = useState("1");
  const b = parseFloat(bill), t = parseFloat(tip), s = parseInt(split) || 1;
  const tipAmt = (!isNaN(b) && !isNaN(t)) ? (b * t / 100) : null;
  const total = tipAmt !== null ? b + tipAmt : null;
  const perPerson = total !== null ? total / s : null;

  const quickTips = [15, 18, 20, 25];

  return (
    <div>
      <p style={{ fontSize: 15, color: "#6B7280", marginTop: 0, marginBottom: 20, lineHeight: 1.6 }}>
        Calculate tip and split the bill.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
        <NumInput label="Bill Amount" value={bill} onChange={setBill} placeholder="45.00" prefix="$" />
        <NumInput label="Tip %" value={tip} onChange={setTip} placeholder="18" suffix="%" />
        <NumInput label="Split By" value={split} onChange={setSplit} placeholder="1" />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {quickTips.map(q => (
          <button key={q} onClick={() => setTip(q.toString())} style={{
            flex: 1, padding: "8px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
            background: parseInt(tip) === q ? rgba(SKY, 0.1) : "rgba(255,255,255,0.02)",
            border: parseInt(tip) === q ? `1.5px solid ${rgba(SKY, 0.3)}` : "1.5px solid rgba(255,255,255,0.05)",
            fontSize: 13, fontWeight: 600, color: parseInt(tip) === q ? SKY_LIGHT : "#6B7280",
          }}>{q}%</button>
        ))}
      </div>

      {tipAmt !== null && (
        <div style={{ display: "grid", gridTemplateColumns: s > 1 ? "1fr 1fr 1fr" : "1fr 1fr", gap: 12 }}>
          <Result label="Tip" value={`$${formatNum(tipAmt, 2)}`} />
          <Result label="Total" value={`$${formatNum(total, 2)}`} color="#10B981" />
          {s > 1 && <Result label="Per Person" value={`$${formatNum(perPerson, 2)}`} sub={`${s} people`} color="#C4B5FD" />}
        </div>
      )}
    </div>
  );
}

// ─── Main App ───
export default function PercentCalcApp() {
  const [tab, setTab] = useState("of");

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-15%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(SKY, 0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />

      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(SKY, 0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <HomeButton accentColor={SKY} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${SKY}, ${BLUE})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff" }}>%</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${SKY_LIGHT}, #93C5FD)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>PercentCalc</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdStack</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 640, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h1 style={{ fontSize: 38, fontWeight: 800, margin: "0 0 8px", lineHeight: 1.1, letterSpacing: "-1px", background: `linear-gradient(135deg, #E0F2FE, ${SKY_LIGHT} 40%, #93C5FD 70%, #BFDBFE)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Percentage Calculator
          </h1>
          <p style={{ fontSize: 15, color: "#6B7280", margin: 0 }}>Four percentage tools in one. Results update instantly.</p>
        </div>

        {/* Tabs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 28 }}>
          {MODES.map(m => (
            <button key={m.id} onClick={() => setTab(m.id)} style={{
              padding: "14px 8px", borderRadius: 14, cursor: "pointer", fontFamily: "inherit",
              background: tab === m.id ? rgba(SKY, 0.1) : "rgba(255,255,255,0.02)",
              border: tab === m.id ? `1.5px solid ${rgba(SKY, 0.3)}` : "1.5px solid rgba(255,255,255,0.05)",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
              transition: "all 0.2s ease",
            }}>
              <span style={{ fontSize: 18 }}>{m.icon}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: tab === m.id ? SKY_LIGHT : "#6B7280" }}>{m.label}</span>
              <span style={{ fontSize: 10, color: tab === m.id ? SKY : "#374151" }}>{m.desc}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: "24px 22px", borderRadius: 20, background: "rgba(10,11,20,0.6)", border: `1px solid ${rgba(SKY, 0.08)}`, boxShadow: "0 4px 30px rgba(0,0,0,0.2)" }}>
          {tab === "of" && <PercentOf />}
          {tab === "is" && <PercentIs />}
          {tab === "change" && <PercentChange />}
          {tab === "tip" && <TipCalc />}
        </div>

        {/* Quick reference */}
        <div style={{
          marginTop: 28, padding: "18px 20px", borderRadius: 14,
          background: rgba(SKY, 0.03), border: `1px solid ${rgba(SKY, 0.06)}`,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Common Percentages</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px 16px", fontSize: 13 }}>
            {[
              ["10%", "÷ 10"], ["20%", "÷ 5"], ["25%", "÷ 4"], ["33%", "÷ 3"],
              ["50%", "÷ 2"], ["75%", "× 3 ÷ 4"], ["100%", "× 1"], ["200%", "× 2"],
            ].map(([pct, trick], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                <span style={{ color: SKY_LIGHT, fontWeight: 600 }}>{pct}</span>
                <span style={{ color: "#4B5563" }}>{trick}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Fraction to percent reference */}
        <div style={{
          marginTop: 12, padding: "18px 20px", borderRadius: 14,
          background: rgba(SKY, 0.03), border: `1px solid ${rgba(SKY, 0.06)}`,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Fraction to Percent</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px 16px", fontSize: 13 }}>
            {[
              ["1/2", "50%"], ["1/3", "33.3%"], ["1/4", "25%"], ["1/5", "20%"],
              ["1/6", "16.7%"], ["1/8", "12.5%"], ["1/10", "10%"], ["3/4", "75%"],
            ].map(([frac, pct], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                <span style={{ color: "#6B7280" }}>{frac}</span>
                <span style={{ color: SKY_LIGHT, fontWeight: 600 }}>{pct}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Info cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 32 }}>
          {[
            { icon: "🔢", title: "% of Number", text: "What is 15% of 200?" },
            { icon: "📊", title: "X is what %", text: "30 is what % of 200?" },
            { icon: "📈", title: "% Change", text: "From 80 to 120 = ?" },
            { icon: "💰", title: "Tip & Split", text: "Bill, tip %, and split" },
          ].map((item, i) => (
            <div key={i} style={{ padding: "16px 12px", borderRadius: 14, background: rgba(SKY, 0.02), border: `1px solid ${rgba(SKY, 0.05)}` }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{item.icon}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: SKY_LIGHT, marginBottom: 3 }}>{item.title}</div>
              <div style={{ fontSize: 10, color: "#6B7280", lineHeight: 1.5 }}>{item.text}</div>
            </div>
          ))}
        </div>

        {/* Cross promo */}
        <div style={{ marginTop: 32, padding: "20px", borderRadius: 16, background: `linear-gradient(135deg, ${rgba(SKY, 0.06)}, ${rgba(BLUE, 0.06)})`, border: `1px solid ${rgba(SKY, 0.12)}`, textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: SKY_LIGHT, marginBottom: 6 }}>Need more study tools?</div>
          {/* Cross-promo (rotates per page load) */}
          <CrossPromo currentSlug="percent" accent={SKY} accent2={BLUE} accentLight={SKY_LIGHT} />
        </div>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(SKY, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="tools" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; }
        input[type="number"] { -moz-appearance: textfield; }
        input::placeholder { color: #374151; }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
